import { useSyncExternalStore } from 'react';
import type React from 'react';

import { useInView } from 'react-intersection-observer';

import { cn } from '@change/ds-utils';

/**
 * Decorative: the link text carries the meaning. The ring is removed (not paused) under reduced
 * motion so no frozen half-scaled ring is left. `[transform-box:fill-box]` makes it scale from its
 * own centre; SVG otherwise scales from the viewport's top-left.
 *
 * The ring pauses while the dot is off-screen or the tab is hidden, and resumes where it stopped.
 */
export const PulseDot: React.FC<EmptyProps> = () => {
	const { ref, inView } = useInView();
	const documentVisible = useSyncExternalStore(subscribeToVisibility, isDocumentVisible, isDocumentVisibleOnServer);

	return (
		<svg
			ref={ref}
			width="8"
			height="8"
			viewBox="0 0 8 8"
			aria-hidden="true"
			focusable="false"
			className="text-icon-brand mt-1.5 flex-none self-start overflow-visible"
		>
			<circle
				cx="4"
				cy="4"
				r="4"
				fill="currentColor"
				className={cn(
					'animate-spike-pulse origin-center [transform-box:fill-box] motion-reduce:hidden',
					(!inView || !documentVisible) && '[animation-play-state:paused]',
				)}
				data-testid="pulse-dot-ring"
			/>
			<circle cx="4" cy="4" r="4" fill="currentColor" />
		</svg>
	);
};

function subscribeToVisibility(onChange: () => void): () => void {
	document.addEventListener('visibilitychange', onChange);
	return () => document.removeEventListener('visibilitychange', onChange);
}

function isDocumentVisible(): boolean {
	return document.visibilityState === 'visible';
}

function isDocumentVisibleOnServer(): boolean {
	return true;
}
